import { RouterProvider } from '@tanstack/react-router';
import { InfoCircle } from 'iconsax-reactjs';
import { useAuthStatus } from '@/lib/useAuth';
import { LoginPage } from '@/routes/LoginPage';
import { router } from '@/router';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/Button';
import { useI18n } from '@/lib/i18n';

/**
 * Decides, once, whether to show the login screen or the app.
 *
 * Doing this above the router means unauthenticated visitors never mount an app
 * route, so there is no window in which a protected screen renders before a
 * redirect fires.
 */
function AuthGate() {
  const { data: status, isPending, isError, error, refetch } = useAuthStatus();
  const { t } = useI18n();

  if (isPending) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4">
        <Logo className="h-7 animate-pulse" />
        <span className="sr-only">{t('app.loading')}</span>
      </div>
    );
  }

  // Almost always means the API process is down. Say so plainly rather than
  // bouncing the user to a login screen that also will not work.
  if (isError) {
    return (
      <div className="flex min-h-dvh items-center justify-center px-4">
        <div className="max-w-sm space-y-4 text-center">
          <div className="mx-auto flex size-12 items-center justify-center rounded-full bg-danger/10 text-danger">
            <InfoCircle size={24} variant="Bold" />
          </div>
          <h1 className="text-lg font-semibold">{t('app.unreachable')}</h1>
          <p className="text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'The API did not respond.'}
          </p>
          <Button variant="secondary" onClick={() => void refetch()}>
            Try again
          </Button>
        </div>
      </div>
    );
  }

  if (!status?.authenticated) {
    return <LoginPage />;
  }

  return <RouterProvider router={router} />;
}

export function App() {
  return <AuthGate />;
}
